import { Box, Typography } from '@mui/material'
import TextInput from './TextInput'
import DateInput from './DateInput'
import SwitchInput from './SwitchInput'
import MapInput from './MapInput'
import {
    AttributeType,
    BUSINESS_RULES_FIELD_ID,
} from '../../constants/constants'
import EntityTypeAttribute from '../Entity/EntityInstance/EntityInstanceFields/EntityTypeAttribute'
import NumberInput from './NumberInput '
import RulesTriggerField from '../DTStudio/BusinessRules/RulesTriggerField'
import TextAreaInput from './TextAreaInput'
import NumberInputAndUnits from './NumberInputAndUnits'
import JsonTypeWithEntity from '../Entity/EntityInstance/EntityInstanceFields/JsonTypeWithEntity'
import FileUploadInput from './FileUploadInput'
import CurrencyField from './CurrencyField'
import TimeAndZoneSelect from './TimeAndZoneSelect'
import SearchAndSelectInput from './SearchAndSelectInput'
import SearchAndMultiSelectInput from './SearchAndMultiSelectInput'
import SealineAttribute from './SealineAttribute'
import UserTypeAttribute from './UserTypeAttribute'
import UserGroupTypeAttribute from './UserGroupTypeAttribute'
import EmailInput from './EmailInput'
import EmailInputMulti from './EmailInputMulti'
import { useTranslation } from 'react-i18next'
import { ReactElement, useEffect, useMemo, useRef } from 'react'
import DeviceRelationAttribute from '../Entity/EntityInstance/EntityInstanceFields/DeviceRelationAttribute'
import BusinessRulesetAttribute from '../Entity/EntityInstance/EntityInstanceFields/BusinessRulesetAttribute'
import OrganizationSelectField from './OrganizationSelectField'
import OrganizationMultiSelectField from './OrganizationMultiSelectField'

interface FieldsType {
    fields: any
    onChangeFields: any
    onClickMap?: any
    disabled?: boolean
    orgId?: string // some attribute have API calls (enity, entityJson, JSon, users, userGroups, ruleset) for this need orgId
    fieldMinWidth?: string
    fieldMaxWidth?: string
}

const Fields = (props: FieldsType): ReactElement => {
    const {
        fields,
        onChangeFields,
        onClickMap,
        disabled,
        orgId,
        fieldMinWidth,
        fieldMaxWidth,
    } = props

    const { t } = useTranslation()

    const firstInputRef = useRef<HTMLInputElement | null>(null)
    const hasFocused = useRef(false)

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (firstInputRef.current && !hasFocused.current) {
                firstInputRef.current.focus()
                hasFocused.current = true
            }
        }, 100)

        return () => clearTimeout(timeout)
    }, [fields])

    const firstFocusableIndex = useMemo(
        () =>
            fields?.findIndex(
                (item: any) => !item.hide && !(disabled || item.disabled),
            ),
        [fields, disabled],
    )
    return (
        <Box
            width="100%"
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                width: '100% !important',
                '& > *': {
                    flex: `1 1 ${fieldMaxWidth || 'calc(50% - 20px)'}`,
                    maxWidth: fieldMaxWidth || 'calc(50% - 20px)',
                    minWidth: fieldMinWidth || '250px',
                    marginBottom: '20px',
                    marginRight: '20px',
                },
            }}
        >
            {fields?.map((item: any, index: any) => {
                let inputComponent: JSX.Element
                const inputRef =
                    index === firstFocusableIndex ? firstInputRef : undefined
                switch (item.dataType.toLowerCase()) {
                    case AttributeType.STRING:
                        inputComponent = (
                            <TextInput
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                type={'text'}
                                required={item.required}
                                disabled={disabled || item.disabled}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                endAdornment={item.unit}
                                ref={inputRef}
                            />
                        )
                        break
                    case AttributeType.NUMBER:
                        inputComponent = (
                            <NumberInput
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                type="number"
                                required={item.required}
                                disabled={disabled || item.disabled}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                range={item.range}
                                endAdornment={item.unit}
                                ref={inputRef}
                            />
                        )
                        break
                    case AttributeType.DECIMAL:
                    case AttributeType.THRESHOLD:
                        inputComponent = (
                            <NumberInput
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                type="decimal"
                                required={item.required}
                                disabled={disabled || item.disabled}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                range={item.range}
                                endAdornment={item.unit}
                                ref={inputRef}
                            />
                        )
                        break
                    case AttributeType.SELECT:
                    case AttributeType.RADIO:
                    case AttributeType.COUNTRYISOCODE:
                        inputComponent = (
                            <SearchAndSelectInput
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onSelect={onChangeFields}
                                list={item.list}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                required={item.required}
                                disabled={disabled || item.disabled}
                            />
                        )
                        break
                    case AttributeType.MULTI_SELECT:
                        inputComponent = (
                            <SearchAndMultiSelectInput
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                list={item.list}
                                required={item.required}
                                disabled={disabled || item.disabled}
                                onSelect={onChangeFields}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                            />
                        )
                        break
                    case AttributeType.DATE:
                        inputComponent = (
                            <DateInput
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                required={item.required}
                                disabled={disabled || item.disabled}
                                clearable={true}
                            />
                        )
                        break
                    case AttributeType.DATE_TIME:
                        inputComponent = (
                            <TimeAndZoneSelect
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                required={item.required}
                                disabled={disabled || item.disabled}
                                clearable={true}
                            />
                        )
                        break
                    case AttributeType.BOOLEAN:
                        inputComponent = (
                            <SwitchInput
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                required={item.required}
                                disabled={disabled || item.disabled}
                            />
                        )
                        break
                    case AttributeType.FILE:
                        inputComponent = (
                            <FileUploadInput
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                required={item.required}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                disabled={disabled || item.disabled}
                            />
                        )
                        break
                    case AttributeType.GEO_FENCING:
                        inputComponent = (
                            <MapInput
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                required={item.required}
                                onClickMap={(val: boolean) => onClickMap?.(val)}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                disabled={disabled || item.disabled}
                            />
                        )
                        break
                    case AttributeType.CURRENCY:
                        inputComponent = (
                            <CurrencyField
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                type={item.inputType || 'decimal'}
                                defaultUnits={item.unit}
                                required={item.required}
                                disabled={disabled || item.disabled}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                range={item.range}
                            />
                        )
                        break
                    case AttributeType.TEMPERATURE:
                    case AttributeType.HUMIDITY:
                    case AttributeType.WEIGHT:
                    case AttributeType.DISTANCE:
                        inputComponent = (
                            <NumberInputAndUnits
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                type="number"
                                dataType={item.dataType}
                                defaultUnits={item.unit}
                                required={item.required}
                                disabled={disabled || item.disabled}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                range={item.range}
                            />
                        )
                        break
                    case AttributeType.USER:
                        inputComponent = (
                            <UserTypeAttribute
                                label={t(item.name)}
                                id={item.id}
                                value={item.value || []}
                                onChange={onChangeFields}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                required={item.required}
                                disabled={disabled || item.disabled}
                            />
                        )
                        break
                    case AttributeType.USER_GROUP:
                        inputComponent = (
                            <UserGroupTypeAttribute
                                label={t(item.name)}
                                id={item.id}
                                value={item.value || []}
                                onChange={onChangeFields}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                required={item.required}
                                disabled={disabled || item.disabled}
                            />
                        )
                        break
                    case AttributeType.ENTITY:
                    case AttributeType.Entity_JSON:
                        inputComponent = (
                            <EntityTypeAttribute
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                type={item.dataType}
                                templateId={item.range}
                                onChange={onChangeFields}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                required={item.required}
                                disabled={disabled || item.disabled}
                                isArray={item?.metadata?.Array}
                                orgId={orgId || ''}
                            />
                        )
                        break

                    case AttributeType.JSON:
                        inputComponent = (
                            <JsonTypeWithEntity
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                templateId={item.range}
                                onChange={onChangeFields}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                required={item.required}
                                disabled={disabled || item.disabled}
                                isArray={item?.metadata?.Array}
                                orgId={orgId || ''}
                            />
                        )
                        break
                    case AttributeType.RULESET:
                        inputComponent = (
                            <BusinessRulesetAttribute
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                templateId={item.range}
                                orgId={orgId || ''}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                required={item.required}
                                disabled={disabled || item.disabled}
                            />
                        )
                        break
                    case AttributeType.RULES_FIELD_TRIGGER:
                        inputComponent = (
                            <RulesTriggerField
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                required={item.required}
                                disabled={disabled || item.disabled}
                            />
                        )
                        break
                    case AttributeType.TEXT_AREA:
                        inputComponent = (
                            <TextAreaInput
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                required={item.required}
                                disabled={disabled || item.disabled}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                            />
                        )
                        break
                    case AttributeType.SEALINE:
                    case AttributeType.FACILITY:
                        inputComponent = (
                            <SealineAttribute
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                type={item.dataType}
                                required={item.required}
                                disabled={disabled || item.disabled}
                                onChange={onChangeFields}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                            />
                        )
                        break
                    case AttributeType.EMAIL:
                        inputComponent = item?.metadata?.Array ? (
                            <EmailInputMulti
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                required={item.required}
                                disabled={disabled || item.disabled}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                            />
                        ) : (
                            <EmailInput
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                required={item.required}
                                disabled={disabled || item.disabled}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                            />
                        )
                        break
                    case AttributeType.DEVICE_RELATION:
                        inputComponent = (
                            <DeviceRelationAttribute
                                label={t(item.displayName)}
                                id={item.id}
                                value={item.value}
                                templateId={item.templateId}
                                parentTemplateId={item.parentTemplateId}
                                onChange={onChangeFields}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                required={item.required}
                                disabled={disabled || item.disabled}
                                orgId={orgId || ''}
                                selectedEntities={item.selectedEntities}
                            />
                        )
                        break
                    case AttributeType.ORGANIZATION_SELECT:
                        inputComponent = (
                            <OrganizationSelectField
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                required={item.required}
                                disabled={disabled || item.disabled}
                                excludeOrgs={item.excludeOrgs}
                            />
                        )
                        break
                    case AttributeType.ORGANIZATION_MULTI_SELECT:
                        inputComponent = (
                            <OrganizationMultiSelectField
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                required={item.required}
                                disabled={disabled || item.disabled}
                                excludeOrgs={item.excludeOrgs}
                            />
                        )
                        break
                    default:
                        inputComponent = (
                            <TextInput
                                label={t(item.name)}
                                id={item.id}
                                value={item.value}
                                onChange={onChangeFields}
                                type="text"
                                required={item.required}
                                disabled={disabled || item.disabled}
                                errorMessage={
                                    item.errorMessage
                                        ? t(item.errorMessage)
                                        : ''
                                }
                                endAdornment={item.unit}
                                ref={inputRef}
                            />
                        )
                }

                const renderFieldWrapper = () => {
                    if (item.hide) {
                        return null
                    }

                    if (item.id === BUSINESS_RULES_FIELD_ID) {
                        return (
                            <Box
                                sx={{
                                    minWidth: 'calc(100% - 20px) !important',
                                    marginTop: '5px',
                                }}
                            >
                                <Box sx={{ marginBottom: '15px' }}>
                                    <Typography
                                        sx={{
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: '#000',
                                        }}
                                    >
                                        {t('businessRules')}
                                    </Typography>
                                </Box>
                                <Box
                                    sx={{
                                        minWidth: '250px',
                                        width: 'calc(50% - 20px)',
                                        maxWidth: '850px',
                                        marginBottom: '20px',
                                        marginRight: '20px',
                                    }}
                                    key={index + item.id}
                                >
                                    <BusinessRulesetAttribute
                                        label={t(item.name)}
                                        id={item.id}
                                        value={item.value}
                                        onChange={onChangeFields}
                                        templateId={item.templateId}
                                        orgId={orgId || ''}
                                        errorMessage={
                                            item.errorMessage
                                                ? t(item.errorMessage)
                                                : ''
                                        }
                                        required={item.required}
                                        disabled={disabled || item.disabled}
                                    />
                                </Box>
                            </Box>
                        )
                    }

                    if (
                        item.dataType.toLowerCase() === AttributeType.SEPARATOR
                    ) {
                        return (
                            <Box
                                sx={{
                                    minWidth: 'calc(100% - 20px) !important',
                                    margin: '0px !important',
                                    height: '0px',
                                }}
                                key={index + item.id}
                                className="separator-line"
                            ></Box>
                        )
                    }
                    return (
                        <Box
                            key={index + item.id}
                            sx={{
                                ...(item.width && {
                                    width: `calc(${item.width}) !important`,
                                    minWidth: `calc(${item.width}) !important`,
                                }),
                            }}
                        >
                            {inputComponent}
                        </Box>
                    )
                }

                return renderFieldWrapper()
            })}
        </Box>
    )
}

export default Fields
